import { Button } from "@msuite/ui/button";
import { ContentCard } from "@msuite/ui/content-card";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { SettingsItem } from "@msuite/ui/settings";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import React from "react";
import { useNavigate } from "react-router";
import { client } from "@/hono";
import type { UserPanelBaseProps } from "../../types";

export function UserSettingsGeneral({ userId }: UserPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const navigate = useNavigate();
	const qc = useQueryClient();

	/** Functions */
	async function handleOnRemoveUserFromOrganization() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			toast.promise(
				async () => {
					const response = await client.organizations.users[":userId"].$delete({
						param: {
							userId,
						},
					});
					await responseHandler(response);
					await qc.invalidateQueries({
						predicate: invalidate("users"),
					});
					navigate("/users");
				},
				{
					loading: t("general.loading"),
					success: t("users.remove_user_from_organization_successfully"),
					error: (error) => t(error.message),
				},
			);
		} catch (error) {
			console.error(error);
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<ContentCard
				title="Danger Zone"
				icon={icon("Skull")}
			>
				<SettingsItem
					title={t("users.remove_user_from_organization")}
					description={t("users.remove_user_from_organization_description")}
				>
					<Button
						variant="danger-ghost"
						onClick={handleOnRemoveUserFromOrganization}
						data-cy="remove-button"
					>
						{t("general.remove")}
					</Button>
				</SettingsItem>
			</ContentCard>
			<ConfirmModal
				controller={asyncModal}
				message={t("users.remove_user_from_organization_confirm_message")}
			/>
		</React.Fragment>
	);
}
